import React, { useEffect, useState } from 'react';
import { FC } from 'react';
import { DateRangePicker, RangeKeyDict } from 'react-date-range';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'hooks/useClickAway';
import { dateFormat, getLocaleSet } from 'utils/ui';
import withErrorBoundary from 'components/ui/withErrorBoundaries';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import MovablePlaceholder from '../MovablePlaceholder/MovablePlaceholder';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateRange, DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

interface FormGroupDaterangePickerProps {
  onChange: (item: { startDate: string; endDate: string }) => void;
  className?: string;
  value: {
    startDate: string;
    endDate: string;
  };
  label?: string;
  disabled?: boolean;
  format?: string;
  showSeparateCalendars?: boolean;
  movablePlaceholder?: string;
  movablePlaceholderIcon?: any;
  locale?: string;
}

const FormGroupDaterangePicker: FC<FormGroupDaterangePickerProps> = ({
  className = '',
  showSeparateCalendars = false,
  ...props
}) => {
  const [t] = useTranslation();
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const ref = useClickAway(() => setOpen(false));
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState({
    startDate: new Date(),
    endDate: new Date(),
  });
  const [initialValue] = useState(() => ({
    startDate: new Date(props.value.startDate),
    endDate: new Date(props.value.endDate),
  }));
  const [startCalendarViewDate, setStartCalendarViewDate] = useState(
    () => new Date(props.value.startDate)
  );
  const [endCalendarViewDate, setEndCalendarViewDate] = useState(() => new Date(props.value.endDate));
  const dayPickerLocale = getLocaleSet(props.locale) as any;
  const startMonth = new Date(2000, 0, 1);
  const endMonth = new Date(2100, 11, 31);

  const format = props.format
    ? props.format
    : "EEE '<span class=''text-bold text-uppercase''>'dd MMM'</span>,' yy";

  const openDatepicker = () => {
    if (props.disabled) {
      return;
    }
    setOpen(true);
  };

  useEffect(() => {
    const { startDate, endDate } = props.value;
    const nextStartDate = new Date(startDate);
    const nextEndDate = new Date(endDate);
    const newValue = {
      startDate: nextStartDate,
      endDate: nextEndDate,
    };
    setValue(newValue);
  }, [props.value]);

  const onChange = (nextRange: DateRange | undefined) => {
    if (!nextRange?.from) {
      return;
    }

    const nextStartDate = nextRange.from;
    const nextEndDate = nextRange.to || nextRange.from;

    setValue({
      startDate: nextStartDate,
      endDate: nextEndDate,
    });

    props.onChange({
      startDate: dateFormat(nextStartDate, 'yyyy-MM-dd', 'en'),
      endDate: dateFormat(nextEndDate, 'yyyy-MM-dd', 'en'),
    });
  };

  const onChangeDefault = (nextValue: RangeKeyDict) => {
    if (nextValue['range1'].endDate && nextValue['range1'].startDate) {
      const nextStartDate = nextValue['range1'].startDate;
      const nextEndDate = nextValue['range1'].endDate;

      setValue({
        startDate: nextStartDate,
        endDate: nextEndDate,
      });

      props.onChange({
        startDate: dateFormat(nextStartDate, 'yyyy-MM-dd', 'en'),
        endDate: dateFormat(nextEndDate, 'yyyy-MM-dd', 'en'),
      });
    }
  };

  const onReset = () => {
    setValue(initialValue);
    setStartCalendarViewDate(initialValue.startDate);
    setEndCalendarViewDate(initialValue.endDate);
    props.onChange({
      startDate: dateFormat(initialValue.startDate, 'yyyy-MM-dd', 'en'),
      endDate: dateFormat(initialValue.endDate, 'yyyy-MM-dd', 'en'),
    });
  };

  const selectedRange: DateRange = {
    from: value.startDate,
    to: value.endDate,
  };

  return (
    <div className={className}>
      <div className={'form-group'}>
        {!!props.label && <label>{props.label}</label>}
        <div
          className={
            'form-control form-group-calendar' +
            (props.movablePlaceholder ? ' movable-placeholder-input' : '')
          }
          onClick={openDatepicker}
        >
          <span dangerouslySetInnerHTML={{ __html: dateFormat(value.startDate, format) }}></span>{' '}
          <span className="ph-5">-</span>
          <span dangerouslySetInnerHTML={{ __html: dateFormat(value.endDate, format) }}></span>
        </div>

        {props.movablePlaceholder && (
          <MovablePlaceholder
            label={props.movablePlaceholder}
            showSmall={open || !!value.startDate}
            icon={props.movablePlaceholderIcon}
          />
        )}

        {open && (
          <div ref={ref} className="daterangepicker-wr">
            {showSeparateCalendars ? (
              <div className="daterangepicker-separate">
                <div>
                  <DayPicker
                    mode="range"
                    selected={selectedRange}
                    onSelect={onChange}
                    month={startCalendarViewDate}
                    onMonthChange={setStartCalendarViewDate}
                    locale={dayPickerLocale}
                    numberOfMonths={1}
                    captionLayout="dropdown"
                    startMonth={startMonth}
                    endMonth={endMonth}
                    showOutsideDays={true}
                    fixedWeeks={true}
                  />
                </div>
                <div>
                  <DayPicker
                    mode="range"
                    selected={selectedRange}
                    onSelect={onChange}
                    month={endCalendarViewDate}
                    onMonthChange={setEndCalendarViewDate}
                    locale={dayPickerLocale}
                    numberOfMonths={1}
                    captionLayout="dropdown"
                    startMonth={startMonth}
                    endMonth={endMonth}
                    showOutsideDays={true}
                    fixedWeeks={true}
                  />
                </div>
              </div>
            ) : (
              <DateRangePicker
                ranges={[{ ...value, color: colorContrast }]}
                onChange={onChangeDefault}
                months={2}
                direction="horizontal"
                locale={dayPickerLocale}
                preventSnapRefocus={true}
              />
            )}
            <div className="daterangepicker-bottom">
              <div className="flex-row mb-15" style={{ alignItems: 'center' }}>
                <div className="flex-column">
                  <div className="text-light mb-5">{t('search.check_in')}</div>
                  <div className="text-bold">
                    {dateFormat(value.startDate, 'EEE - dd MMM, yyyy')}
                  </div>
                </div>
                <span className="ph-20">
                  <FontAwesomeIcon icon={faArrowRight} />
                </span>
                <div className="flex-column">
                  <div className="text-light mb-5">{t('search.check_out')}</div>
                  <div className="text-bold">{dateFormat(value.endDate, 'EEE - dd MMM, yyyy')}</div>
                </div>
              </div>
              <div className="flex-between" style={{ gap: '10px' }}>
                <button className="btn  btn-primary" type="button" onClick={() => setOpen(false)}>
                  {t('choose')}
                </button>
                <button className="btn  btn-link" type="button" onClick={onReset}>
                  {t('reset')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withErrorBoundary(FormGroupDaterangePicker, 'FormGroupDaterangePicker');
